import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiService } from '../services/api';
import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import usePackageStore from '../stores/packageStore';
import useUserStore from '../stores/userStore';
import CurrencyIcon from './CurrencyIcon';

const Container = styled.div`
	height: 100vh;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	background: ${props => props.theme.background};
	display: flex;
	flex-direction: column;
	padding-top: 50px;
`;

const Content = styled.div`
	flex: 1;
	padding: 16px;
	display: flex;
	flex-direction: column;
`;

const Title = styled.h1`
	font-size: 24px;
	font-weight: 700;
	color: ${props => props.theme.text.primary};
	text-align: center;
	margin: 0 0 16px 0;
`;

const Subtitle = styled.p`
	font-size: 16px;
	color: ${props => props.theme.text.secondary};
	text-align: center;
	margin: 0 0 32px;
`;

const TokenIcon = styled.div`
	font-size: 64px;
	text-align: center;
	margin-bottom: 32px;
`;

const PacksList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const PackItem = styled.button`
	background: ${props => props.theme.card.background};
	border: none;
	border-radius: 12px;
	padding: 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	cursor: pointer;
	width: 100%;
	text-align: left;

	&:active {
		opacity: 0.8;
	}
`;

const PackLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const PackAmount = styled.span`
	color: ${props => props.theme.text.primary};
	font-size: 16px;
	font-weight: 500;
`;

const PackRight = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const PackPrice = styled.span`
	color: ${props => props.theme.text.secondary};
	font-size: 14px;
`;

const PackBonus = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	color: #FFD700;
	font-size: 14px;
`;

const Terms = styled.p`
	font-size: 13px;
	color: ${props => props.theme.text.secondary};
	text-align: center;
	margin-top: 32px;
`;

const Packages = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const user = useUserStore(state => state.user);
	const { packages, loading, error, fetchPackages, wasLoaded } = usePackageStore();
	const [abortController, setAbortController] = useState(null);
	const pollingTimerRef = useRef(null);
	const fetchUser = useUserStore(state => state.fetchUser);

	useEffect(() => {
		if (WebApp.platform !== 'unknown') {
			WebApp.BackButton.show();
			WebApp.BackButton.onClick(() => {
				navigate(-1);
			});
		}

		return () => {
			if (WebApp.platform !== 'unknown') {
				WebApp.BackButton.hide();
				WebApp.BackButton.offClick();
			}
		};
	}, [navigate]);

	// Добавляем обработчик события invoice_closed
	useEffect(() => {
		const handleInvoiceClosed = (event) => {
			console.log('Invoice closed event:', event);

			// Останавливаем polling при любом закрытии инвойса
			if (abortController) {
				abortController.abort();
				setAbortController(null);
			}

			if (event.status === 'paid') {
				console.log('Payment successful via event handler');
				// Обработка успешного платежа будет в callback openInvoice
			} else if (event.status === 'failed') {
				console.error('Payment failed via event handler');
				toast.error('Оплата не удалась. Пожалуйста, попробуйте еще раз.', {
					position: "top-center",
					theme: "dark",
					style: {
						background: 'rgba(0, 0, 0, 0.8)',
						backdropFilter: 'blur(10px)',
						WebkitBackdropFilter: 'blur(10px)',
						borderRadius: '12px',
						border: '1px solid rgba(255, 255, 255, 0.1)'
					}
				});
			} else if (event.status === 'cancelled') {
				console.log('Payment cancelled via event handler');
				toast.info('Оплата была отменена', {
					position: "top-center",
					theme: "dark",
					style: {
						background: 'rgba(0, 0, 0, 0.8)',
						backdropFilter: 'blur(10px)',
						WebkitBackdropFilter: 'blur(10px)',
						borderRadius: '12px',
						border: '1px solid rgba(255, 255, 255, 0.1)'
					}
				});
			}
		};

		WebApp.onEvent('invoice_closed', handleInvoiceClosed);

		return () => {
			WebApp.offEvent('invoice_closed', handleInvoiceClosed);
			// Очищаем AbortController при размонтировании компонента
			if (abortController) {
				abortController.abort();
				setAbortController(null);
			}
		};
	}, [abortController]);

	// --- Новый Long Polling ---
	const pollTransactionStatus = async (transactionId, pack, telegramId) => {
		const controller = new AbortController();
		setAbortController(controller);

		return new Promise((resolve, reject) => {
			setTimeout(async () => {
				try {
					const response = await apiService.getTransactionStatus(transactionId, { signal: controller.signal });
					const status = response?.status;
					console.log('Transaction response:', response);
					console.log('Transaction status:', status);
					if (status === 'success') {
						toast.success(
							`Поздравляем! Вы получили ${pack.tokens.toLocaleString()} токенов!`,
							{
								position: "top-center",
								autoClose: 2000,
								style: {
									background: 'rgba(0, 0, 0, 0.8)',
									backdropFilter: 'blur(10px)',
									WebkitBackdropFilter: 'blur(10px)',
									borderRadius: '12px',
									border: '1px solid rgba(255, 255, 255, 0.1)'
								}
							}
						);
						try {
							await fetchUser();
						} catch (e) { }
						resolve('success');
						return;
					}
					if (status === 'failed') {
						toast.error('Оплата не удалась. Пожалуйста, попробуйте еще раз.', {
							position: "top-center",
							theme: "dark",
							style: {
								background: 'rgba(0, 0, 0, 0.8)',
								backdropFilter: 'blur(10px)',
								WebkitBackdropFilter: 'blur(10px)',
								borderRadius: '12px',
								border: '1px solid rgba(255, 255, 255, 0.1)'
							}
						});
						reject('failed');
						return;
					}
					toast.info('Не удалось подтвердить транзакцию. Попробуйте позже', {
						position: "top-center",
						theme: "dark",
						style: {
							background: 'rgba(0, 0, 0, 0.8)',
							backdropFilter: 'blur(10px)',
							WebkitBackdropFilter: 'blur(10px)',
							borderRadius: '12px',
							border: '1px solid rgba(255, 255, 255, 0.1)'
						}
					});
					reject('unknown');
				} catch (err) {
					if (err.name === 'AbortError') {
						return;
					}
					console.error('Ошибка при проверке статуса транзакции:', err);
					toast.error('Ошибка при проверке статуса транзакции', {
						position: "top-center",
						theme: "dark"
					});
					reject('error');
				}
			}, 3000);
		});
	};

	const handleBuy = async (pack) => {
		try {
			console.log('Starting purchase process for pack:', pack);
			console.log('WebApp init data:', {
				initData: WebApp.initData,
				initDataUnsafe: WebApp.initDataUnsafe,
				version: WebApp.version,
				platform: WebApp.platform
			});

			// Проверяем, что у нас есть telegram_id
			const telegramId = WebApp.initDataUnsafe?.user?.id;
			if (!telegramId) {
				console.error('No Telegram ID available');
				toast.error('Ошибка: не удалось получить ID пользователя', {
					position: "top-center",
					theme: "dark",
					style: {
						background: 'rgba(0, 0, 0, 0.8)',
						backdropFilter: 'blur(10px)',
						WebkitBackdropFilter: 'blur(10px)',
						borderRadius: '12px',
						border: '1px solid rgba(255, 255, 255, 0.1)'
					}
				});
				return;
			}

			// Проверяем версию WebApp
			if (WebApp.version < 6.9) {
				console.error('Unsupported WebApp version:', WebApp.version);
				toast.error('Пожалуйста, обновите Telegram до последней версии', {
					position: "top-center",
					theme: "dark",
					style: {
						background: 'rgba(0, 0, 0, 0.8)',
						backdropFilter: 'blur(10px)',
						WebkitBackdropFilter: 'blur(10px)',
						borderRadius: '12px',
						border: '1px solid rgba(255, 255, 255, 0.1)'
					}
				});
				return;
			}

			// Проверяем наличие всех необходимых данных в пакете
			if (!pack.id || !pack.stars || !pack.tokens) {
				console.error('Invalid pack data:', pack);
				toast.error('Ошибка: неверные данные пакета', {
					position: "top-center",
					theme: "dark",
					style: {
						background: 'rgba(0, 0, 0, 0.8)',
						backdropFilter: 'blur(10px)',
						WebkitBackdropFilter: 'blur(10px)',
						borderRadius: '12px',
						border: '1px solid rgba(255, 255, 255, 0.1)'
					}
				});
				return;
			}

			// Создаем инвойс
			console.log('Creating invoice for pack ID:', pack.id, 'and telegram_id:', telegramId);
			const invoiceResponse = await apiService.createInvoiceLink(pack.id);
			console.log('Invoice response:', invoiceResponse);

			// Получаем ссылку на инвойс и transaction_id
			const invoiceLink = invoiceResponse.data?.invoice_link || invoiceResponse.invoice_link;
			const transactionId = invoiceResponse.data?.transaction_id || invoiceResponse.transaction_id;

			console.log('Invoice link:', invoiceLink);
			console.log('Transaction ID:', transactionId);

			if (!invoiceLink) {
				console.error('Invalid invoice response:', invoiceResponse);
				throw new Error('Failed to create invoice: Invalid response format');
			}

			if (!transactionId) {
				console.error('No transaction ID in response:', invoiceResponse);
				throw new Error('Failed to create invoice: No transaction ID');
			}

			console.log('Opening invoice with link:', invoiceLink);

			WebApp.openInvoice(invoiceLink, async (status) => {
				console.log('Payment callback status:', status);
				if (status === 'paid') {
					// Запускаем polling статуса транзакции
					if (transactionId) {
						try {
							await pollTransactionStatus(transactionId, pack, telegramId);
							await fetchUser(); // обновляем баланс после успешного polling
							navigate(-1);
						} catch (pollError) {
							console.error('Ошибка polling:', pollError);
							await fetchUser(); // обновляем баланс даже если polling не удался
						}
					} else {
						await fetchUser(); // обновляем баланс если нет transactionId
						toast.info('Платёж прошёл, но не удалось проверить статус. Попробуйте обновить баланс позже.', {
							position: "bottom-center",
							theme: "dark",
							style: {
								background: 'rgba(0, 0, 0, 0.8)',
								backdropFilter: 'blur(10px)',
								WebkitBackdropFilter: 'blur(10px)',
								borderRadius: '12px',
								border: '1px solid rgba(255, 255, 255, 0.1)',
								marginBottom: 'env(safe-area-inset-bottom, 24px)'
							}
						});
					}
				}
			});
		} catch (error) {
			console.error('Error during purchase process:', error);
			console.error('Error details:', {
				message: error.message,
				stack: error.stack,
				response: error.response?.data
			});
			toast.error('Произошла ошибка. Попробуйте позже.', {
				position: "top-center",
				theme: "dark",
				style: {
					background: 'rgba(0, 0, 0, 0.8)',
					backdropFilter: 'blur(10px)',
					WebkitBackdropFilter: 'blur(10px)',
					borderRadius: '12px',
					border: '1px solid rgba(255, 255, 255, 0.1)'
				}
			});
		}
	};

	if (loading) {
		return (
			<Container>
				<Content>
					{t('common.loading')}
				</Content>
			</Container>
		);
	}

	return (
		<Container>
			<Content>
				<TokenIcon>
					<CurrencyIcon size={128} />
				</TokenIcon>
				<Title>{t('buyTokens', { currency: t('currency_title') })}</Title>
				<Subtitle>{t('profile.topUpBalance')}</Subtitle>

				{error ? (
					<div>{t('common.error')}</div>
				) : (
					<PacksList>
						{packages && packages.map((pack) => (
							<PackItem key={pack.id} onClick={() => handleBuy(pack)}>
								<PackLeft>
									<PackAmount>{pack.tokens.toLocaleString()} <CurrencyIcon size={16} /></PackAmount>
									{pack.bonus > 0 && (
										<PackBonus>
											+{pack.bonus.toLocaleString()} <CurrencyIcon size={16} />
										</PackBonus>
									)}
								</PackLeft>
								<PackRight>
									<PackPrice>{pack.stars} ⭐</PackPrice>
								</PackRight>
							</PackItem>
						))}
					</PacksList>
				)}

				<Terms>
					{t('store.terms')}
				</Terms>
			</Content>
		</Container>
	);
};

export default Packages; 